import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import {
  faSearch,
  faUserGroup,
  faCircleInfo,
  faArrowRightLong,
  faEye,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { catchError, combineLatest, map, BehaviorSubject, filter } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ActionsService } from 'src/app/services/actions.service';
import { SelectionModel } from '@angular/cdk/collections';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActionDetailedInfoComponent } from 'src/app/components/action-detailed-info/action-detailed-info.component';
import { ApproveFormComponent } from 'src/app/components/approve-form/approve-form.component';
import { RejectFormComponent } from 'src/app/components/reject-form/reject-form.component';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CompleteActionComponent } from 'src/app/components/complete-action/complete-action.component';
import { HandlerSnackbarService } from 'src/app/services/handler-snackbar.service';

export interface Action {
  id: string;
  title: string;
  submittedAt: string;
  categoryName: string;
  applicationName: string;
  author: string;
}
@Component({
  selector: 'app-actions',
  templateUrl: './actions.component.html',
  styleUrls: ['./actions.component.scss'],
})
export class ActionsComponent implements OnInit {
  faCircleInfo = faCircleInfo;
  faArrow = faArrowRightLong;
  faUserGroup = faUserGroup;
  faEye = faEye;
  faTruck = faTruck;
  checkedAll: boolean = false;
  displayedColumns: string[] = [
    'check',
    'submission',
    'type',
    'id',
    'author',
    'step',
    'actions',
    'complete',
  ];
  displayedColumnsRenewal: string[] = [
    'check',
    'submission',
    'type',
    'id',
    'author',
    'step',
    'actions',
  ];
  displayedColumnsTransfer: string[] = [
    'check',
    'submission',
    'type',
    'id',
    'author',
    'step',
    'actions',
  ];
  displayedColumnsDeprovisioning: string[] = [
    'check',
    'submission',
    'type',
    'id',
    'author',
    'step',
    'actions',
  ];
  displayedColumnsHist: string[] = [
    'submitted',
    'type',
    'id',
    'author',
    'step',
    'actions',
  ];
  pendingDataSource: any;
  renewalsDataSource: any;
  transfersDataSource: any;
  deprovisioningsDataSource: any;
  historyDataSource: any;
  pendingSelection = new SelectionModel<any>(true, []);
  renewalSelection = new SelectionModel<any>(true, []);
  transferSelection = new SelectionModel<any>(true, []);
  deprovisioningSelection = new SelectionModel<any>(true, []);
  faSearch = faSearch;
  tab: string = 'pending';
  throttle = 0;
  distance = 2;
  pageSize = 20;
  pageNumber = 0;

  @ViewChild(MatSort) sortPending = new MatSort();
  @ViewChild(MatSort) sortRenewals = new MatSort();
  @ViewChild(MatSort) sortTransfers = new MatSort();
  @ViewChild(MatSort) sortDeprovisionings = new MatSort();
  @ViewChild(MatSort) sortHistory = new MatSort();
  nearEndScroll$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  nextPageToken$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private readonly actionService: ActionsService,
    public dialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private readonly modalService: NgbModal,
    private readonly utilities: UtilitiesService,
    private readonly spinnerService: SpinnerService,
    private readonly snackBarService: HandlerSnackbarService
  ) {
    combineLatest([this.nearEndScroll$, this.nextPageToken$])
      .pipe(
        filter(
          ([nearEndScroll, nextPageToken]) => nearEndScroll && nextPageToken
        ),
        map(([nearEndScroll, nextPageToken]) => {
          this.loadHistoryActions(nextPageToken);
          this.nearEndScroll$.next(null);
          this.nextPageToken$.next(null);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.spinnerService.open();
    this.actionService.getAllPendingActions();
    this.getAllPendingActions();
    this.getAllRenewalsActions();
    this.getAllTransfersActions();
    this.getAllDeprovisioningsActions();
  }

  initSort() {
    this.cdRef.detectChanges();
    if (this.pendingDataSource) this.pendingDataSource.sort = this.sortPending;
    if (this.renewalsDataSource)
      this.renewalsDataSource.sort = this.sortRenewals;
    if (this.transfersDataSource)
      this.transfersDataSource.sort = this.sortTransfers;
    if (this.deprovisioningsDataSource)
      this.deprovisioningsDataSource.sort = this.sortDeprovisionings;
    if (this.historyDataSource) this.historyDataSource.sort = this.sortHistory;

    this.cdRef.detectChanges();
  }

  getAllPendingActions() {
    this.actionService
      .getPendingActionList()
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return this.snackBarService.handlerErrorSubscribe(error);
        })
      )
      .subscribe((data: any) => {
        data.forEach((action: any) => {
          action['checked'] = false;
        });
        this.pendingDataSource = new MatTableDataSource(data);
        this.pendingDataSource.sort = this.sortPending;
        this.pendingDataSource.sortingDataAccessor = (
          item: any,
          property: string
        ) => {
          if (property == "submittedAt") return this.utilities.transformDate(item.submittedAt);
          return item[property];
        };
        this.spinnerService.close();
      });
  }

  getAllRenewalsActions() {
    this.actionService
      .getRenewalActionList()
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return this.snackBarService.handlerErrorSubscribe(error);
        })
      )
      .subscribe((data: any) => {
        data.forEach((action: any) => {
          action['checked'] = false;
        });
        this.renewalsDataSource = new MatTableDataSource(data);
        this.renewalsDataSource.sort = this.sortRenewals;
        this.renewalsDataSource.sortingDataAccessor = (
          item: any,
          property: string
        ) => {
          if (property == "submittedAt") return this.utilities.transformDate(item.submittedAt);
          return item[property];
        };
        this.spinnerService.close();
      });
  }

  getAllTransfersActions() {
    this.actionService
      .getTransferActionList()
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return this.snackBarService.handlerErrorSubscribe(error);
        })
      )
      .subscribe((data: any) => {
        data.forEach((action: any) => {
          action['checked'] = false;
        });
        this.transfersDataSource = new MatTableDataSource(data);
        this.transfersDataSource.sort = this.sortTransfers;
        this.transfersDataSource.sortingDataAccessor = (
          item: any,
          property: string
        ) => {
          if (property == "submittedAt") return this.utilities.transformDate(item.submittedAt);
          return item[property];
        };
        this.spinnerService.close();
      });
  }

  getAllDeprovisioningsActions() {
    this.actionService
      .getDeprovisioningActionList()
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return this.snackBarService.handlerErrorSubscribe(error);
        })
      )
      .subscribe((data: any) => {
        data.forEach((action: any) => {
          action['checked'] = false;
        });
        this.deprovisioningsDataSource = new MatTableDataSource(data);
        this.deprovisioningsDataSource.sort = this.sortDeprovisionings;
        this.deprovisioningsDataSource.sortingDataAccessor = (
          item: any,
          property: string
        ) => {

          if (property == "submittedAt") return this.utilities.transformDate(item.submittedAt);
          return item[property];
        };
        this.spinnerService.close();
      });
  }

  getAllHistoryActions() {
    this.actionService.getAllHistoryActions().subscribe({
      next: (data: any) => {
        const paramNextToken = data?.['nextPageToken'];
        this.nextPageToken$.next(paramNextToken);
        this.historyDataSource = new MatTableDataSource(data?.items || []);
        this.historyDataSource.sort = this.sortHistory;
        this.historyDataSource.sortingDataAccessor = (
          item: any,
          property: string
        ) => {
          if (property == "submittedAt") return this.utilities.transformDate(item.submittedAt);
          return item[property];
        };

        this.spinnerService.close();

        // il setTimeout è necessario perché l'initiateInfiniteScrolling va richiamato quando la lista si è refreshata in view
        setTimeout(() => {
          this.initiateInfiniteScrolling();
        }, 0);

      },
      error: (error: HttpErrorResponse) => {
        console.error(error);
        this.snackBarService.viewUserClosingMessage(
          error.error?.error?.message || error.message,
          'error-snackbar'
        );
      },
    });
  }

  initiateInfiniteScrolling() {
    // se non c'è overflow e quindi non è presente scrollbar simula il nearEnd e triggera l'infinite scrolling
    if (document.getElementsByClassName('contents-table')[0].scrollHeight <= (document.getElementsByClassName('contents-table')[0] as HTMLElement).offsetHeight) {
      this.onNearEndScroll();
    }
  }

  onNearEndScroll(): void {
    this.nearEndScroll$.next(true);
  }

  loadHistoryActions(nextPageToken?: string) {
    document.body.style.cursor = 'progress';
    this.actionService.getAllHistoryActions(nextPageToken).subscribe({
      next: (data: any) => {
        const paramNextToken = data?.['nextPageToken'];
        this.nextPageToken$.next(paramNextToken);
        const retrieveLoadedData = this.historyDataSource?.data || [];
        this.historyDataSource = new MatTableDataSource([
          ...retrieveLoadedData,
          ...(data.items || []),
        ]);
        this.historyDataSource.sort = this.sortHistory;
        this.historyDataSource.sortingDataAccessor = (
          item: any,
          property: string
        ) => {
          if (property == "submittedAt") return this.utilities.transformDate(item.submittedAt);
          return item[property];
        };
        document.body.style.cursor = 'default';
      },
      error: (error: HttpErrorResponse) => {
        console.error(error);
        document.body.style.cursor = 'default';
        this.snackBarService.viewUserClosingMessage(
          error.error.error.message || error.message,
          'error-snackbar'
        );
      },
    });
  }

  refreshActions() {
    this.spinnerService.open();
    switch (this.tab) {
      case 'pending':
        this.pendingSelection.clear();
        this.actionService.getAllPendingActions();
        break;
      case 'renewals':
        this.renewalSelection.clear();
        this.actionService.getAllRenewalsActions();
        break;
      case 'transfers':
        this.transferSelection.clear();
        this.actionService.getAllTransfersActions();
        break;
      case 'deprovisionings':
        this.deprovisioningSelection.clear();
        this.actionService.getAllDeprovisioningsActions();
        break;
      case 'history':
        this.getAllHistoryActions();
        break;
      default:
        this.spinnerService.close();
    }
  }

  approveActions(actionList: string[]) {
    this.actionService
      .approveActions(actionList)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return this.snackBarService.handlerErrorSubscribe(error);
        })
      )
      .subscribe((data: any) => {
        this.getAllPendingActions();
        this.snackBarService.viewUserClosingMessage(
          'Operazione conclusa con successo',
          'withdraw-snackbar'
        );
      });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    switch (this.tab) {
      case 'pending':
        this.pendingDataSource.filter = filterValue.trim().toLowerCase();
        break;
      case 'renewals':
        this.renewalsDataSource.filter = filterValue.trim().toLowerCase();
        break;
      case 'transfers':
        this.transfersDataSource.filter = filterValue.trim().toLowerCase();
        break;
      case 'deprovisionings':
        this.deprovisioningsDataSource.filter = filterValue
          .trim()
          .toLowerCase();
        break;
      case 'history':
        this.historyDataSource.filter = filterValue.trim().toLowerCase();
        break;
      default:
        this.pendingDataSource.filter = filterValue.trim().toLowerCase();
    }
  }

  approveSelected() {
    const modalRef = this.modalService.open(ApproveFormComponent, {
      size: 'md',
      centered: true,
    });
    modalRef.result.then(
      (result) => {
        if (result) {
          this.spinnerService.open();
          this.snackBarService.viewUserMessage();
          const length = result.selection._selected.length;
          for (let i = 0; i < result.selection._selected.length; i++) {
            const action = result.selection._selected[i];
            this.actionService
              .approveAction(action.id, result.message)
              .pipe(
                catchError((error: HttpErrorResponse) => {
                  return this.snackBarService.handlerErrorSubscribe(error);
                })
              )
              .subscribe((data: any) => {
                i === length - 1 &&
                  setTimeout(() => this.refreshActions(), 2000);
                this.snackBarService.viewUserClosingMessage(
                  'Operazione conclusa con successo',
                  'withdraw-snackbar'
                );
              });
          }
        }
        this.clearSelection();
      },
      (error) => {
        console.log(error);
      }
    );

    modalRef.componentInstance.selection = this.getTypeSelection();
  }

  clearSelection() {
    this.getTypeSelection().clear();
  }

  getTypeSelection() {
    switch (this.tab) {
      case 'pending':
        return this.pendingSelection;
      case 'renewals':
        return this.renewalSelection;
      case 'transfers':
        return this.transferSelection;
      case 'deprovisionings':
        return this.deprovisioningSelection;
      default:
        return this.pendingSelection;
    }
  }

  rejectSelected() {
    const modalRef = this.modalService.open(RejectFormComponent, {
      size: 'md',
      centered: true,
    });
    modalRef.result.then(
      (result) => {
        if (result) {
          this.spinnerService.open();
          const length = result.selection._selected.length;
          this.snackBarService.viewUserMessage();
          for (let i = 0; i < result.selection._selected.length; i++) {
            const action = result.selection._selected[i];
            this.actionService
              .rejectAction(action.id, result.message)
              .pipe(
                catchError((error: HttpErrorResponse) => {
                  return this.snackBarService.handlerErrorSubscribe(error);
                })
              )
              .subscribe((data: any) => {
                i === length - 1 && this.refreshActions();
                this.snackBarService.viewUserClosingMessage(
                  'Operazione conclusa con successo',
                  'withdraw-snackbar'
                );
              });
          }
        }
        this.clearSelection();
      },
      (error) => {
        console.log(error);
      }
    );

    modalRef.componentInstance.selection = this.getTypeSelection();
  }

  isAllPendingSelected() {
    const numSelected = this.pendingSelection.selected.length;
    const numRows = this.pendingDataSource.data.length;
    return numSelected === numRows;
  }

  pendingMasterToggle() {
    this.isAllPendingSelected()
      ? this.pendingSelection.clear()
      : this.pendingDataSource.data
        .filter((element: any) => !this.isOnDeliveryStatus(element))
        .forEach((row: any) => this.pendingSelection.select(row));
  }

  isAllRenewalSelected() {
    const numSelected = this.renewalSelection.selected.length;
    const numRows = this.renewalsDataSource.data.length;
    return numSelected === numRows;
  }

  isAllTransferSelected() {
    const numSelected = this.transferSelection.selected.length;
    const numRows = this.transfersDataSource.data.length;
    return numSelected === numRows;
  }

  isAllDeprovisioningSelected() {
    const numSelected = this.deprovisioningSelection.selected.length;
    const numRows = this.deprovisioningsDataSource.data.length;
    return numSelected === numRows;
  }

  renewalMasterToggle() {
    this.isAllRenewalSelected()
      ? this.renewalSelection.clear()
      : this.renewalsDataSource.data.forEach((row: any) =>
        this.renewalSelection.select(row)
      );
  }

  transferMasterToggle() {
    this.isAllTransferSelected()
      ? this.transferSelection.clear()
      : this.transfersDataSource.data.forEach((row: any) =>
        this.transferSelection.select(row)
      );
  }

  deprovisioningMasterToggle() {
    this.isAllDeprovisioningSelected()
      ? this.deprovisioningSelection.clear()
      : this.deprovisioningsDataSource.data.forEach((row: any) =>
        this.deprovisioningSelection.select(row)
      );
  }

  tabChange(selTab: string) {
    this.tab = selTab;
    this.refreshActions();
    this.initSort();
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  openInfo(element: any) {
    const modalRef = this.modalService.open(ActionDetailedInfoComponent, {
      size: 'md',
      centered: true,
    });
    modalRef.result.then(
      (result) => {
        if (result) {
          this.snackBarService.viewUserMessage();
          this.actionService
            .getPendingAction(result.id)
            .pipe(
              catchError((error: HttpErrorResponse) => {
                return this.snackBarService.handlerErrorSubscribe(error);
              })
            )
            .subscribe((data: any) => {
              console.log(data);
            });
        }
      },
      (error) => {
        console.log(error);
      }
    );

    modalRef.componentInstance.info = element;
  }

  isOnDeliveryStatus(element: any) {
    return element.status.some(
      (el: any) => el.status === 'pending' && el.description === 'Delivery'
    );
  }

  openCompleteAction(element: any) {
    const modalRef = this.modalService.open(CompleteActionComponent, {
      size: 'md',
      centered: true,
    });
    modalRef.componentInstance.item = element;
    modalRef.result.then(
      (result) => {
        if (result) {
          this.spinnerService.open();
          this.snackBarService.viewUserMessage();
          this.actionService
            .completeAction(element.id, result.form)
            .pipe(
              catchError((error: HttpErrorResponse) => {
                return this.snackBarService.handlerErrorSubscribe(error);
              })
            )
            .subscribe((data: any) => {
              setTimeout(() => this.refreshActions(), 2000);
              this.snackBarService.viewUserClosingMessage(
                'Operazione conclusa con successo',
                'withdraw-snackbar'
              );
            });
        }
      },
      (error) => {
        console.log(error);
      }
    );
  }
}
